import { useCallback, useEffect, useRef, useState } from 'react';
import { ChapterText, ContinueButton, PlayerOrb, Revelation, Whisper } from '../zen-ui';

const HOLD_MS = 1600;
const DECAY_MS = 900;
const RING_R = 80;
const RING_C = 2 * Math.PI * RING_R;

/** Chapter I — there are no accounts. Press and hold to kindle your key into light. */
export function KindleKey({ onComplete }: { onComplete: () => void }) {
  const [progress, setProgress] = useState(0);
  const [lit, setLit] = useState(false);
  const [npub, setNpub] = useState('');
  const holdingRef = useRef(false);
  const progressRef = useRef(0);
  const lastRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const ignite = useCallback(() => {
    const charset = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
    const id = Array.from(
      { length: 8 },
      () => charset[Math.floor(Math.random() * charset.length)],
    ).join('');
    setNpub(`npub1${id}`);
    setLit(true);
  }, []);

  const tick = useCallback(
    (t: number) => {
      const dt = t - lastRef.current;
      lastRef.current = t;
      const next = holdingRef.current
        ? progressRef.current + dt / HOLD_MS
        : progressRef.current - dt / DECAY_MS;
      const clamped = Math.max(0, Math.min(1, next));
      progressRef.current = clamped;
      setProgress(clamped);
      if (clamped >= 1) {
        ignite();
        return;
      }
      if (clamped > 0 || holdingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    },
    [ignite],
  );

  const startHold = useCallback(() => {
    if (lit) return;
    holdingRef.current = true;
    lastRef.current = performance.now();
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [lit, tick]);

  const endHold = useCallback(() => {
    holdingRef.current = false;
  }, []);

  return (
    <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center gap-12 px-6 py-24 text-center">
      <ChapterText numeral="I · the key" title="there are no accounts here.">
        On Nostr, you are a secret key — a number so vast it could never be
        guessed. Hold still, and kindle your light.
      </ChapterText>

      <div className="animate-zen-fade-up" style={{ animationDelay: '800ms' }}>
        <button
          type="button"
          aria-label="press and hold to kindle your light"
          onPointerDown={startHold}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          onPointerCancel={endHold}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
              e.preventDefault();
              startHold();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === ' ' || e.key === 'Enter') endHold();
          }}
          className="relative grid touch-none select-none place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60"
          style={{ width: 180, height: 180 }}
        >
          <svg
            width="180"
            height="180"
            viewBox="0 0 180 180"
            className="absolute inset-0 -rotate-90"
            aria-hidden="true"
          >
            <circle
              cx="90"
              cy="90"
              r={RING_R}
              fill="none"
              stroke="rgba(255,255,255,0.09)"
              strokeWidth="1.5"
            />
            <circle
              cx="90"
              cy="90"
              r={RING_R}
              fill="none"
              stroke="rgba(253,224,171,0.85)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - progress)}
            />
          </svg>

          {lit && (
            <div className="pointer-events-none absolute inset-0 animate-zen-ripple rounded-full border border-amber-200/50" />
          )}

          {lit ? (
            <PlayerOrb size={92} className="animate-zen-fade-in" />
          ) : (
            <div
              className="rounded-full"
              style={{
                width: 14 + progress * 30,
                height: 14 + progress * 30,
                background:
                  'radial-gradient(circle at 35% 30%, #fff8e7, #f6d493 55%, #c9963f)',
                opacity: 0.25 + progress * 0.75,
                boxShadow: `0 0 ${10 + progress * 44}px rgba(246, 212, 147, ${0.2 + progress * 0.5})`,
                transition: 'width 90ms linear, height 90ms linear, opacity 90ms linear, box-shadow 90ms linear',
              }}
            />
          )}
        </button>
      </div>

      {!lit ? (
        <Whisper delay={1100}>press and hold</Whisper>
      ) : (
        <div className="flex flex-col items-center gap-6">
          <Revelation delay={200}>
            {npub} — this light is yours alone.
          </Revelation>
          <Whisper delay={700}>no one issued it · no one can take it away</Whisper>
          <ContinueButton onClick={onComplete} delay={1200} />
        </div>
      )}
    </div>
  );
}
